import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PencilIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { whatsappService, WhatsAppConversation } from '../../services/whatsappService';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export default function WhatsAppTab() {
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<'anonymize' | 'extract' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedConv, setSelectedConv] = useState<WhatsAppConversation | null>(null);
  const [selectedExtracted, setSelectedExtracted] = useState<{ filename: string; content: string } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // ConfirmDialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteFilename, setPendingDeleteFilename] = useState<string | null>(null);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasPendingAnonymize = conversations.some(c => c.status === 'uploaded');
  const hasPendingExtract = conversations.some(c => c.status === 'approved');

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await whatsappService.listConversations();
      setConversations(response.conversations);
    } catch (err: any) {
      setError('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Ao montar o componente, verificar se já há extração em andamento (usuário voltou à página)
  useEffect(() => {
    let cancelled = false;
    whatsappService.getExtractionStatus().then(status => {
      if (cancelled || !status.running) return;
      setProcessing(true);
      setProcessingAction('extract');
      const fileInfo = status.current_file ? ` — ${status.current_file}` : '';
      const chunkInfo = status.total_chunks > 1 ? ` (bloco ${status.current_chunk}/${status.total_chunks})` : '';
      setMessage(`Extração em andamento... ${status.processed}/${status.total} concluídas${fileInfo}${chunkInfo}`);
      startExtractionPolling();
    }).catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    setIsDragging(false);
    setError(null);
    setMessage(null);

    try {
      // Validate files
      for (const file of files) {
        const validation = whatsappService.validateFile(file);
        if (!validation.valid) {
          setError(validation.error || 'Arquivo inválido');
          setUploading(false);
          return;
        }
      }

      const response = await whatsappService.uploadConversations(files);
      setMessage(response.message);
      await loadConversations();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao fazer upload');
    } finally {
      setUploading(false);
    }
  }, [loadConversations]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await uploadFiles(files);
    e.target.value = '';
  }, [uploadFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    await uploadFiles(files);
  }, [uploadFiles]);

  const handleViewExtracted = async (conv: WhatsAppConversation) => {
    setLoading(true);
    setError(null);
    try {
      const extracted = await whatsappService.getExtractedConversation(conv.id);
      setSelectedExtracted({
        filename: extracted.knowledge_filename,
        content: extracted.content,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao carregar conteúdo extraído');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymize = async () => {
    setProcessing(true);
    setProcessingAction('anonymize');
    setError(null);
    setMessage(null);

    try {
      const response = await whatsappService.anonymizeConversations();
      setMessage(response.message);
      await loadConversations();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao anonimizar');
    } finally {
      setProcessing(false);
      setProcessingAction(null);
    }
  };

  const handleViewConversation = async (conv: WhatsAppConversation) => {
    setLoading(true);
    try {
      const fullConv = await whatsappService.getConversation(conv.id);
      setSelectedConv(fullConv);
      setEditContent(fullConv.content || '');
      setAdminNotes(fullConv.admin_notes || '');
      setEditMode(false);
    } catch (err: any) {
      setError('Erro ao carregar conversa');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedConv) return;

    setLoading(true);
    setError(null);

    try {
      await whatsappService.updateConversation(selectedConv.id, editContent, adminNotes);
      setMessage('Conversa atualizada com sucesso');
      setEditMode(false);
      await loadConversations();
      const updated = await whatsappService.getConversation(selectedConv.id);
      setSelectedConv(updated);
    } catch (err: any) {
      setError('Erro ao salvar alterações');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await whatsappService.approveConversation(id);
      setMessage('Conversa aprovada');
      await loadConversations();
      if (selectedConv?.id === id) {
        const updated = await whatsappService.getConversation(id);
        setSelectedConv(updated);
      }
    } catch (err: any) {
      setError('Erro ao aprovar conversa');
    }
  };

  const handleDiscard = async (id: string) => {
    try {
      await whatsappService.discardConversation(id);
      setMessage('Conversa descartada');
      await loadConversations();
      if (selectedConv?.id === id) {
        setSelectedConv(null);
      }
    } catch (err: any) {
      setError('Erro ao descartar conversa');
    }
  };

  const handleDelete = (id: string, filename: string) => {
    setPendingDeleteId(id);
    setPendingDeleteFilename(filename);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (!pendingDeleteId) return;
    try {
      await whatsappService.deleteConversation(pendingDeleteId);
      setMessage('Conversa deletada permanentemente');
      if (selectedConv?.id === pendingDeleteId) setSelectedConv(null);
      await loadConversations();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao deletar conversa');
    } finally {
      setPendingDeleteId(null);
      setPendingDeleteFilename(null);
    }
  };

  const startExtractionPolling = useCallback(() => {
    // Guard: clear any existing interval before starting a new one
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    return new Promise<void>((resolve) => {
      let consecutiveErrors = 0;

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const status = await whatsappService.getExtractionStatus();
          consecutiveErrors = 0;

          if (status.running) {
            // Re-sync state on every tick — guards against spurious resets
            setProcessing(true);
            setProcessingAction('extract');
            const fileInfo = status.current_file ? ` — ${status.current_file}` : '';
            const chunkInfo = status.total_chunks > 1
              ? ` (bloco ${status.current_chunk}/${status.total_chunks})`
              : '';
            setMessage(`Extraindo... ${status.processed}/${status.total} concluídas${fileInfo}${chunkInfo}`);
          } else {
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            if (status.errors.length > 0) {
              setError(`Erros: ${status.errors.join('; ')}`);
            }
            if (status.processed > 0) {
              setMessage(`${status.processed} conversa(s) extraída(s) com sucesso`);
            }
            await loadConversations();
            setProcessing(false);
            setProcessingAction(null);
            resolve();
          }
        } catch (err: any) {
          const httpStatus = err?.response?.status;
          if (httpStatus === 401) {
            // Token expired — stop polling silently, axios interceptor handles refresh/redirect
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            resolve();
            return;
          }
          consecutiveErrors++;
          if (consecutiveErrors >= 3) {
            // Give up after 3 consecutive transient errors
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            setProcessing(false);
            setProcessingAction(null);
            resolve();
          }
          // Otherwise keep retrying on transient errors
        }
      }, 3000);
    });
  }, [loadConversations]);

  const handleExtract = async () => {
    setError(null);
    setMessage(null);

    try {
      const startResponse = await whatsappService.extractKnowledge();
      if (startResponse.count === 0) {
        setMessage('Nenhuma conversa aprovada para extrair');
        return;
      }
      setProcessing(true);
      setProcessingAction('extract');
      setMessage(`Extraindo ${startResponse.count} conversa(s)... pode navegar à vontade.`);
      await startExtractionPolling();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409) {
        // Já estava rodando — retomar o polling
        setProcessing(true);
        setProcessingAction('extract');
        setMessage('Extração já em andamento, acompanhando...');
        await startExtractionPolling();
      } else {
        setError(detail || 'Erro ao iniciar extração');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      uploaded: 'bg-gray-100 text-gray-800',
      anonymized: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      extracted: 'bg-purple-100 text-purple-800',
      indexed: 'bg-emerald-100 text-emerald-800',
      processed: 'bg-emerald-100 text-emerald-800',
      discarded: 'bg-red-100 text-red-800',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      uploaded: 'Enviado',
      anonymized: 'Anonimizado',
      approved: 'Aprovado',
      extracted: 'Extraído',
      indexed: 'Indexado no RAG',
      processed: 'Processado',
      discarded: 'Descartado',
    };
    return labels[status as keyof typeof labels] || status;
  };

  return (
    <div className="space-y-6">
      {/* Messages */}
      {message && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700">{message}</p>
          </div>
        </div>
      )}

      <div className="rounded-md bg-indigo-50 p-4">
        <p className="text-sm text-indigo-700">
          <strong>Fluxo:</strong> <code>Extrair Conhecimento</code> gera arquivos curados pela LLM, mas eles só entram no RAG após clicar em <strong>Indexar Novos Documentos</strong> na seção abaixo.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {processingAction && (
        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex items-center">
            <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />
            <p className="ml-3 text-sm text-blue-700">
              {processingAction === 'extract'
                ? 'Extraindo conhecimento com LLM... Aguarde a conclusão.'
                : 'Anonimizando conversas... Aguarde a conclusão.'}
            </p>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div
        className={`bg-white shadow rounded-lg p-6 border-2 border-dashed transition-colors ${isDragging
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-transparent'
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex items-center gap-2 mb-4">
          <ArrowUpTrayIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Upload de Conversas</h3>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Arraste e solte arquivos .txt aqui ou clique em <strong>browse...</strong> para selecionar manualmente.
          </p>

          <label className="block">
            <span className="sr-only">Escolher arquivos</span>
            <input
              type="file"
              multiple
              accept=".txt"
              onChange={handleFileUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-emerald-50 file:text-emerald-700
                hover:file:bg-emerald-100
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </label>

          <div className="flex gap-2">
            <button
              onClick={handleAnonymize}
              disabled={processing || uploading || !hasPendingAnonymize}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingAction === 'anonymize' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <SparklesIcon className="h-4 w-4" />
              )}
              {processingAction === 'anonymize' ? 'Anonimizando...' : 'Anonimizar Pendentes'}
            </button>

            <button
              onClick={handleExtract}
              disabled={processing || uploading || !hasPendingExtract}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingAction === 'extract' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
              )}
              {processingAction === 'extract' ? 'Extraindo...' : 'Extrair Conhecimento'}
            </button>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Conversas ({conversations.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {loading && conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Carregando...</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhuma conversa encontrada. Faça upload de arquivos .txt do WhatsApp.
            </div>
          ) : (
            conversations.map((conv) => (
              <div key={conv.id} className="p-4 hover:bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-gray-900 truncate">{conv.filename}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusBadge(conv.status)}`}>
                        {getStatusLabel(conv.status)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Enviado em {new Date(conv.uploaded_at).toLocaleString('pt-BR')}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleViewConversation(conv)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-700 hover:text-blue-900 whitespace-nowrap"
                    >
                      <EyeIcon className="h-4 w-4" />
                      Ver
                    </button>

                    {(conv.status === 'extracted' || conv.status === 'indexed') && (
                      <button
                        onClick={() => handleViewExtracted(conv)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-purple-700 hover:text-purple-900 bg-purple-50 rounded-md whitespace-nowrap"
                      >
                        <SparklesIcon className="h-4 w-4" />
                        Extraído
                      </button>
                    )}

                    {conv.status === 'anonymized' && (
                      <>
                        <button
                          onClick={() => handleApprove(conv.id)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-green-700 hover:text-green-900 whitespace-nowrap"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Aprovar
                        </button>
                        <button
                          onClick={() => handleDiscard(conv.id)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-700 hover:text-red-900 whitespace-nowrap"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Descartar
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => handleDelete(conv.id, conv.filename)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-400 hover:text-red-700"
                      title="Deletar permanentemente"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Conversation Viewer/Editor Modal */}
      {selectedConv && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{selectedConv.filename}</h3>
                <span className={`mt-1 inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedConv.status)}`}>
                  {getStatusLabel(selectedConv.status)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {!editMode && selectedConv.status === 'anonymized' && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-blue-700 hover:text-blue-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Editar
                  </button>
                )}
                <button
                  onClick={() => setSelectedConv(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conteúdo da Conversa
                    </label>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas do Administrador
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      placeholder="Adicione notas sobre as edições realizadas..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  {selectedConv.content ? (
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                      {selectedConv.content}
                    </pre>
                  ) : (
                    <div className="bg-gray-50 p-6 rounded-md text-center">
                      <p className="text-sm text-gray-500 italic">Conteúdo anonimizado não disponível para esta conversa.</p>
                      {(selectedConv.status === 'extracted' || selectedConv.status === 'indexed') && (
                        <button
                          onClick={() => { setSelectedConv(null); handleViewExtracted(selectedConv); }}
                          className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100"
                        >
                          <SparklesIcon className="h-4 w-4" />
                          Ver Conhecimento Extraído
                        </button>
                      )}
                    </div>
                  )}
                  {selectedConv.admin_notes && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                      <p className="text-sm font-medium text-yellow-800">Notas do Administrador:</p>
                      <p className="mt-1 text-sm text-yellow-700">{selectedConv.admin_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {editMode && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditContent(selectedConv.content || '');
                    setAdminNotes(selectedConv.admin_notes || '');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Salvar Alterações
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Deletar conversa"
        message={`Deletar permanentemente "${pendingDeleteFilename}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Deletar"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Extracted Knowledge Viewer Modal */}
      {selectedExtracted && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Conhecimento extraído</h3>
                <p className="mt-1 text-sm text-gray-500">{selectedExtracted.filename}</p>
              </div>
              <button
                onClick={() => setSelectedExtracted(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                {selectedExtracted.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
